/**
 * `<MapSettingsHome/>` — B6.1, the Tasks-team settings landing.
 *
 * Issue #762 / #754 (Phase 2). Replaces the Phase-0 `MapSettingsPage` stub.
 * Deliberately tiny (§B6.1, the anti-clunkiness promise): a Team section
 * (name + members) and a Map section (who-can-see, reminders, optional
 * features), then a one-line "this is a Tasks team" footer. **No stations /
 * rules / solver / balance pages** — those belong to the scheduling app and
 * never mount here.
 *
 * Faithful to `B6-1-settings-home.svg`: TEAM / MAP group headers with a thin
 * rule under each, a label · value · action layout per row, and the closing
 * italic note. Entry points:
 *   - Members → navigates to `/teams/:id/map/members` (B6.4).
 *   - Who can see the map → opens the B6.2 visibility setting
 *     (`<MapVisibilitySetting/>`) in a dialog; the row value reflects the live
 *     `task_member_branch_only` posture.
 *   - Optional features → opens B6.3 (`<MapOptionalFeatures/>`) in a dialog.
 *
 * Lead-oriented (§B6.1 is *lead/PI*). Role resolves once via
 * `useCurrentMember().isLead`; a non-lead can still read the page but cannot
 * change anything.
 *
 * Brand firewall (§A9): factual copy, no tier/plan language, the privacy
 * posture is stated plainly (the "who can see the map" row is the visible
 * trustable-pillar surface). Isolation: only generic hooks + UI primitives;
 * no scheduler imports.
 *
 * Editorial revamp (#1043, Wave 4, B6.1): theme-aware via `useMapTheme()`. The
 * `task_editorial_ui` flag stays OFF for every team (default LEGACY) until the
 * whole revamp lands, so:
 *   - LEGACY (flag off): byte-identical to the shipped soft-mint settings home
 *     — the teal TEAM/MAP section headers on a mint canvas, the chip-style
 *     "edit"/"open" actions.
 *   - EDITORIAL (flag on): the wireframe `V_B61` — a mono `SETTINGS` kicker over
 *     a serif-italic team-name masthead; TEAM + MAP sections as warm `.t-card`s
 *     with hairline-divided rows (label · value · a `.t-btn.link` action); the
 *     italic "This is a Tasks team." footer note.
 * RE-SKIN, not rebuild: every `data-testid`, copy, and behaviour is preserved.
 */

import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useSetTeamTimezone, useTeam } from "../../api/useTeam";
import { useTeamFeatures } from "../../api/useTeamFeature";
import { useBreadcrumbs } from "../../components/Breadcrumbs";
import { RenameTeamField } from "../../components/RenameTeamField";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Tooltip,
} from "../../components/ui";
import { useCurrentMember } from "../members/useCurrentMember";
import { useTaskTeamMembers } from "../members/useTaskTeamMembers";
import { useMapTheme } from "../theme/MapThemeContext";
import type { MapTheme } from "../theme/tokens";
import { EmailTemplatePreviewSurface } from "./EmailTemplatePreview";
import { MapArchivePanel } from "./MapArchivePanel";
import { MapOptionalFeatures } from "./MapOptionalFeatures";
import { MapVisibilitySetting, VISIBILITY_KEY } from "./MapVisibilitySetting";
import { NotificationPrefsMatrix } from "./NotificationPrefsMatrix";
import { TASK_FEATURE_SPECS } from "./taskFeatureKeys";

const TIMED_NOTICES_LABEL =
  TASK_FEATURE_SPECS.find((s) => s.key === "task_timed_notices")?.label ?? "";

/** The B4.4 per-event x channel notification matrix component key (default OFF). */
const NOTIFICATION_PREFS_KEY = "task_notification_prefs";

const INK = "#0C2A2A";
const MUTED = "#9CA3AF";
const HEADER = "#5C7878";

/**
 * A small, scannable set of common IANA zones for the team-timezone selector
 * (#906), with `Asia/Singapore` (the default, UTC+8) first. Kept short so the
 * `<select>` stays usable; the backend accepts any real IANA zone, so a team
 * with an unusual zone still renders correctly (its current value is appended
 * to the list if it isn't already present).
 */
const COMMON_TIMEZONES = [
  "Asia/Singapore",
  "Asia/Kuala_Lumpur",
  "Asia/Jakarta",
  "Asia/Bangkok",
  "Asia/Hong_Kong",
  "Asia/Tokyo",
  "Asia/Kolkata",
  "Asia/Dubai",
  "Australia/Sydney",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
  "UTC",
] as const;

const DEFAULT_TIMEZONE = "Asia/Singapore";

export function MapSettingsHome() {
  const { teamId } = useParams<{ teamId: string }>();
  const teamQuery = useTeam(teamId);
  const me = useCurrentMember(teamId);
  const { list: members } = useTaskTeamMembers(teamId);
  const featuresQuery = useTeamFeatures(teamId);
  const navigate = useNavigate();
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  const [renaming, setRenaming] = useState(false);
  const [renamedTo, setRenamedTo] = useState<string | null>(null);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const teamName = teamQuery.data?.name ?? "This team";
  const canManage = me.isLead;

  // B4.4 (#1043): the per-event x channel notification matrix is an OPTIONAL
  // layer (`task_notification_prefs`, default OFF). The "Notification settings"
  // row + its dialog appear only when the key is on for this team; otherwise the
  // matrix is unreachable from here (the RemindersBell footer stays its honest
  // "no settings screen" popover). Reads the same features response the rest of
  // the page uses.
  const notificationPrefsOn =
    featuresQuery.data?.features.find((f) => f.key === NOTIFICATION_PREFS_KEY)?.enabled ?? false;

  // #906 — the team's IANA timezone (note timestamps render in it). A lead may
  // change it; a non-lead sees it read-only. The mutation writes the team back
  // into the cache on success so the row + every note stamp pick up the change.
  const teamTimezone = teamQuery.data?.timezone ?? DEFAULT_TIMEZONE;
  const setTimezone = useSetTeamTimezone(teamId);

  // The "Who can see the map" row value reflects the live posture (§A8): the
  // restriction key OFF (default) = whole map shared; ON = own-branch-only.
  // Reads the same features response B6.2's dialog toggles, so the row stays in
  // sync after a change.
  const restrictionOn =
    featuresQuery.data?.features.find((f) => f.key === VISIBILITY_KEY)?.enabled ?? false;
  const visibilityValue = restrictionOn
    ? "Members see only their own branch"
    : "Everyone on the team";

  useBreadcrumbs(
    teamId
      ? [{ label: teamName, to: `/teams/${teamId}/map` }, { label: "Settings" }]
      : [{ label: "Settings" }],
  );

  // The "Optional features" summary line — the few Map features the team has
  // ON, shown as "Name ✓ · …", with anything off as "Name ✗". Mirrors the
  // gallery's "Dependencies ✓ · Notes ✓ · Estimates ✗" sample. Reads from the
  // same features response B6.3 toggles, so it stays in sync after a change.
  const featuresSummary = useMemo(() => {
    const byKey = new Map(featuresQuery.data?.features.map((f) => [f.key, f]) ?? []);
    return TASK_FEATURE_SPECS.map((spec) => {
      const enabled = byKey.get(spec.key)?.enabled ?? false;
      return { label: spec.label, enabled };
    });
  }, [featuresQuery.data]);

  return (
    <div
      data-testid="map-settings-home"
      className="h-full overflow-auto px-8 py-6"
      style={{
        backgroundColor: editorial ? c.canvas : "#EEF6F2",
        fontFamily: editorial ? theme.fonts.sans : undefined,
      }}
    >
      <div className="mx-auto max-w-3xl">
        {/* Editorial masthead (`V_B61`): a mono SETTINGS kicker over a
            serif-italic team-name heading. Legacy has no top heading (the
            section headers carry the structure), so this renders only in
            editorial mode — legacy stays byte-identical. */}
        {editorial && (
          <div className="mb-6">
            <div
              data-testid="settings-kicker"
              className="text-[10px] uppercase"
              style={{ fontFamily: theme.fonts.mono, letterSpacing: "0.1em", color: c.muted }}
            >
              Settings
            </div>
            <h1
              data-testid="settings-team-heading"
              className="mt-1.5 text-[30px] italic leading-tight"
              style={{ fontFamily: theme.fonts.serif, fontWeight: 400, color: c.ink }}
            >
              {teamName}
            </h1>
          </div>
        )}

        {/* TEAM */}
        <SectionHeader theme={theme}>Team</SectionHeader>
        <SettingsCard theme={theme}>
          <SettingsRow
            theme={theme}
            label="Name & display"
            value={
              renaming && teamId && teamQuery.data ? (
                <RenameTeamField
                  teamId={teamId}
                  currentName={teamQuery.data.name}
                  onRenamed={(name) => {
                    setRenaming(false);
                    setRenamedTo(name);
                  }}
                />
              ) : (
                <>
                  {teamName}
                  {renamedTo ? (
                    <output className="ml-2 text-[11px]" data-testid="settings-renamed">
                      Renamed to {renamedTo}.
                    </output>
                  ) : null}
                </>
              )
            }
            action={{
              kind: "button",
              text: renaming ? "cancel" : "edit",
              disabled: !canManage || !teamQuery.data,
              onClick: () => {
                setRenamedTo(null);
                setRenaming((v) => !v);
              },
            }}
            testId="settings-row-name"
          />
          <SettingsRow
            theme={theme}
            label="Members"
            value={`${members.length} ${members.length === 1 ? "person" : "people"}`}
            action={{
              kind: "button",
              text: "open",
              onClick: () => teamId && navigate(`/teams/${teamId}/map/members`),
            }}
            testId="settings-row-members"
          />
          <TimezoneRow
            theme={theme}
            timezone={teamTimezone}
            canManage={canManage}
            isSaving={setTimezone.isPending}
            onChange={(tz) => setTimezone.mutate(tz)}
            last
          />
        </SettingsCard>

        {/* MAP */}
        <div className="mt-6">
          <SectionHeader theme={theme}>Map</SectionHeader>
        </div>
        <SettingsCard theme={theme}>
          <SettingsRow
            theme={theme}
            label="Who can see the map"
            value={visibilityValue}
            action={{
              kind: "button",
              text: "change ▾",
              // B6.2 (visibility setting) opens in a dialog. A non-lead can read
              // the current posture but cannot change it (the dialog itself also
              // renders read-only for a non-lead as a second safety).
              disabled: !canManage,
              onClick: () => setVisibilityOpen(true),
            }}
            testId="settings-row-visibility"
          />
          <SettingsRow
            theme={theme}
            label="Reminders"
            value="In-app + email"
            action={{
              kind: "seam",
              text: "change ▾",
              tooltip: "Reminder settings arrive with the reminders feature.",
            }}
            testId="settings-row-reminders"
          />
          {/* B4.4 — the per-event x channel notification matrix, only when the
              `task_notification_prefs` layer is on for this team. */}
          {notificationPrefsOn && (
            <SettingsRow
              theme={theme}
              label="Notification settings"
              value="What to nudge each person about, and where"
              action={{
                kind: "button",
                text: "open",
                onClick: () => setNotificationsOpen(true),
              }}
              testId="settings-row-notifications"
            />
          )}
          {/* B4.5 — the email-template preview, riding the SAME
              `task_notification_prefs` gate (no new toggle). Honest: it shows the
              email Tarii WILL send once delivery ships — delivery isn't on today. */}
          {notificationPrefsOn && (
            <SettingsRow
              theme={theme}
              label="Email preview"
              value="See the email we’ll send — delivery isn’t switched on yet"
              action={{
                kind: "button",
                text: "preview",
                onClick: () => setEmailPreviewOpen(true),
              }}
              testId="settings-row-email-preview"
            />
          )}
          <SettingsRow
            theme={theme}
            label="Optional features"
            value={<FeaturesSummary items={featuresSummary} />}
            action={{
              kind: "button",
              text: "manage",
              onClick: () => setFeaturesOpen(true),
            }}
            testId="settings-row-features"
            last={!canManage}
          />
          {/* ⑤.b (#1043) — restore-from-archive surface. Lead-only entry point;
              the panel itself never mounts for a non-lead. Sits at the BOTTOM
              of the MAP card so the most-touched settings stay at the top. */}
          {canManage && (
            <SettingsRow
              theme={theme}
              label="Archive"
              value="Restore tasks removed from the map"
              action={{
                kind: "button",
                text: "open",
                onClick: () => setArchiveOpen(true),
              }}
              testId="settings-row-archive"
              last
            />
          )}
        </SettingsCard>

        {/* Tasks-team footer */}
        <div className="mt-5 border-t pt-4" style={{ borderColor: editorial ? c.rule : "#F3F4F6" }}>
          <p className="text-[11px] italic" style={{ color: editorial ? c.muted : MUTED }}>
            This is a Tasks team. (Rostering settings don’t apply.)
          </p>
        </div>
      </div>

      {/* B6.2 — visibility setting, opened from the "change ▾" entry. */}
      <Dialog open={visibilityOpen} onOpenChange={setVisibilityOpen}>
        <DialogContent
          data-testid="map-visibility-dialog"
          className="max-w-lg bg-transparent p-0 shadow-none border-none"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Who can see the map</DialogTitle>
            <DialogDescription>
              Choose how much of the map members see for this team.
            </DialogDescription>
          </DialogHeader>
          <MapVisibilitySetting
            teamId={teamId}
            canManage={canManage}
            onClose={() => setVisibilityOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* B6.3 — optional features, opened from the "manage" entry. */}
      <Dialog open={featuresOpen} onOpenChange={setFeaturesOpen}>
        <DialogContent
          data-testid="map-features-dialog"
          className="max-w-xl p-0"
          style={editorial ? { backgroundColor: c.canvas, borderColor: c.rule } : undefined}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Map features</DialogTitle>
            <DialogDescription>
              Turn the optional Map features on or off for this team.
            </DialogDescription>
          </DialogHeader>
          <MapOptionalFeatures teamId={teamId} canManage={canManage} />
        </DialogContent>
      </Dialog>

      {/* B4.4 — the per-event x channel notification matrix, opened from the
          "Notification settings" row (only mounted when the layer is on). Each
          person sets their OWN matrix (§A9), so it is not lead-gated. */}
      <Dialog open={notificationsOpen} onOpenChange={setNotificationsOpen}>
        <DialogContent
          data-testid="map-notification-prefs-dialog"
          className="max-w-2xl bg-transparent p-0 shadow-none border-none"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Notification settings</DialogTitle>
            <DialogDescription>
              Choose what to be nudged about on the Map, and where.
            </DialogDescription>
          </DialogHeader>
          <div
            className="max-h-[80vh] overflow-auto rounded-2xl p-6"
            style={{
              backgroundColor: editorial ? c.canvas : "#FFFFFF",
              border: `1px solid ${editorial ? c.rule : "#E2E8E5"}`,
            }}
          >
            <NotificationPrefsMatrix teamId={teamId} />
          </div>
        </DialogContent>
      </Dialog>

      {/* ⑤.b (#1043) — the archive panel, opened from the lead-only "Archive"
          entry. The panel itself is presentational; restore goes through the
          shared `useRestoreTaskNode` hook so cache invalidation lives in ONE
          place. The dialog is mounted only when a lead opens it (the entry
          row is hidden for non-leads, so a non-lead never reaches this). */}
      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent
          data-testid="map-archive-dialog"
          className="max-w-xl bg-transparent p-0 shadow-none border-none"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Archive</DialogTitle>
            <DialogDescription>
              Restore tasks that were removed from the map. They keep their history.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[80vh] overflow-auto">
            <MapArchivePanel teamId={teamId} />
          </div>
        </DialogContent>
      </Dialog>

      {/* B4.5 — the email-template preview, opened from the "Email preview" row
          (only mounted when the `task_notification_prefs` layer is on). The
          "change notification settings" link closes this and opens the B4.4
          matrix. FE-only PREVIEW — no email is sent (delivery is deferred). */}
      <Dialog open={emailPreviewOpen} onOpenChange={setEmailPreviewOpen}>
        <DialogContent
          data-testid="map-email-preview-dialog"
          className="max-w-2xl bg-transparent p-0 shadow-none border-none"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Email preview</DialogTitle>
            <DialogDescription>
              A preview of the email Tarii will send for a mention once email delivery is available.
            </DialogDescription>
          </DialogHeader>
          <div
            className="max-h-[80vh] overflow-auto rounded-2xl p-6"
            style={{
              backgroundColor: editorial ? c.canvas : "#FFFFFF",
              border: `1px solid ${editorial ? c.rule : "#E2E8E5"}`,
            }}
          >
            <EmailTemplatePreviewSurface
              onChangeSettings={() => {
                setEmailPreviewOpen(false);
                setNotificationsOpen(true);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SectionHeader({ children, theme }: { children: string; theme: MapTheme }) {
  const editorial = theme.mode === "editorial";
  const c = theme.palette;
  if (editorial) {
    // Editorial: a mono ISSUE label, no underline (the `.t-card` below carries
    // the boundary). Sits as a group label above the card.
    return (
      <div className="mb-2">
        <span
          className="text-[10px] uppercase"
          style={{ fontFamily: theme.fonts.mono, color: c.muted, letterSpacing: "0.1em" }}
        >
          {children}
        </span>
      </div>
    );
  }
  return (
    <div className="border-b pb-2" style={{ borderColor: "#F3F4F6" }}>
      <span
        className="text-[11px] font-bold uppercase"
        style={{ color: HEADER, letterSpacing: "0.5px" }}
      >
        {children}
      </span>
    </div>
  );
}

/**
 * Wraps a settings group. Editorial groups the rows into a warm `.t-card`
 * (cream surface, hairline border); legacy keeps the rows as bare children of
 * the page container (no wrapper) so it stays byte-identical. `marginBottom`
 * on the editorial card keeps the spacing the wireframe shows between groups.
 */
function SettingsCard({ children, theme }: { children: React.ReactNode; theme: MapTheme }) {
  const editorial = theme.mode === "editorial";
  const c = theme.palette;
  if (!editorial) return <>{children}</>;
  return (
    <div
      className="overflow-hidden border"
      style={{ backgroundColor: c.cardBg, borderColor: c.rule, borderRadius: theme.radii.lg }}
    >
      {children}
    </div>
  );
}

interface RowAction {
  /** A real action (lead-only ones pass `disabled`). */
  kind: "button" | "seam";
  text: string;
  onClick?: () => void;
  disabled?: boolean;
  /** Seam-only: explains why the action is not yet live. */
  tooltip?: string;
}

interface SettingsRowProps {
  label: string;
  value: React.ReactNode;
  action: RowAction;
  testId: string;
  theme: MapTheme;
  /** Editorial: the last row in a card omits its bottom hairline. */
  last?: boolean;
}

function SettingsRow({ label, value, action, testId, theme, last }: SettingsRowProps) {
  const editorial = theme.mode === "editorial";
  const c = theme.palette;
  if (editorial) {
    // Editorial row (`V_B61`): label · value · `.t-btn.link` action, divided
    // by hairlines inside the `.t-card`.
    return (
      <div
        data-testid={testId}
        className="flex items-center gap-4 px-[18px] py-3.5"
        style={{ borderBottom: last ? undefined : `1px solid ${c.rule}` }}
      >
        <span className="w-52 shrink-0 text-[12.5px] font-medium" style={{ color: c.ink }}>
          {label}
        </span>
        <span className="min-w-0 flex-1 text-[13px]" style={{ color: c.slate }}>
          {value}
        </span>
        <RowActionControl action={action} label={label} theme={theme} />
      </div>
    );
  }
  return (
    <div data-testid={testId} className="flex items-center gap-4 py-3">
      <span className="w-48 shrink-0 text-xs" style={{ color: INK }}>
        {label}
      </span>
      <span className="min-w-0 flex-1 text-xs" style={{ color: MUTED }}>
        {value}
      </span>
      <RowActionControl action={action} label={label} theme={theme} />
    </div>
  );
}

function RowActionControl({
  action,
  label,
  theme,
}: { action: RowAction; label: string; theme: MapTheme }) {
  const editorial = theme.mode === "editorial";
  const c = theme.palette;

  // Editorial: a `.t-btn.link` — a quiet teal text link, no chip background.
  // Legacy: the mint chip button.
  const baseClass = editorial
    ? "shrink-0 text-[12px] underline-offset-2 transition-opacity hover:underline"
    : "shrink-0 rounded-md px-3 py-1 text-[11px] transition-colors";
  const buttonStyle = editorial
    ? { color: c.teal }
    : {
        backgroundColor: "#F7F8FA",
        border: "1px solid #CFE0D8",
        color: HEADER,
      };

  if (action.kind === "seam") {
    // A disabled <button> swallows pointer events, so Radix's tooltip never
    // fires on it — wrap it in a span the tooltip can attach to.
    return (
      <Tooltip content={action.tooltip ?? null}>
        <span className="shrink-0">
          <button
            type="button"
            data-testid={`settings-action-${label}`}
            data-seam
            disabled
            className={`${baseClass} cursor-not-allowed opacity-60`}
            style={buttonStyle}
          >
            {action.text}
          </button>
        </span>
      </Tooltip>
    );
  }

  return (
    <button
      type="button"
      data-testid={`settings-action-${label}`}
      onClick={action.onClick}
      disabled={action.disabled}
      className={`${baseClass} disabled:cursor-not-allowed disabled:opacity-50 ${
        editorial ? "" : "hover:bg-white"
      }`}
      style={buttonStyle}
    >
      {action.text}
    </button>
  );
}

/**
 * The team-timezone row (#906) — a label · value · action layout matching the
 * other settings rows. A lead sees a `<select>` of common IANA zones (the
 * team's current zone is appended if it isn't one of them, so an unusual zone
 * still shows correctly); a non-lead sees the zone name read-only. Note
 * timestamps across the Map render in this zone.
 */
function TimezoneRow({
  timezone,
  canManage,
  isSaving,
  onChange,
  theme,
  last,
}: {
  timezone: string;
  canManage: boolean;
  isSaving: boolean;
  onChange: (tz: string) => void;
  theme: MapTheme;
  last?: boolean;
}) {
  const editorial = theme.mode === "editorial";
  const c = theme.palette;
  // Ensure the current value is always selectable even if it's outside the
  // common shortlist (the backend accepts any real IANA zone).
  const options = useMemo(() => {
    const list = [...COMMON_TIMEZONES] as string[];
    if (!list.includes(timezone)) list.unshift(timezone);
    return list;
  }, [timezone]);

  return (
    <div
      data-testid="settings-row-timezone"
      className={`flex items-center gap-4 ${editorial ? "px-[18px] py-3.5" : "py-3"}`}
      style={editorial && !last ? { borderBottom: `1px solid ${c.rule}` } : undefined}
    >
      <span
        className={`shrink-0 ${editorial ? "w-52 text-[12.5px] font-medium" : "w-48 text-xs"}`}
        style={{ color: editorial ? c.ink : INK }}
      >
        Timezone
      </span>
      <span
        className={`min-w-0 flex-1 ${editorial ? "text-[13px]" : "text-xs"}`}
        style={{ color: editorial ? c.slate : MUTED }}
      >
        {canManage ? (
          <select
            data-testid="settings-timezone-select"
            value={timezone}
            disabled={isSaving}
            onChange={(e) => onChange(e.target.value)}
            className={`max-w-full px-2 py-1 text-[11px] disabled:cursor-not-allowed disabled:opacity-50 ${
              editorial ? "rounded-md border" : "rounded-md"
            }`}
            style={
              editorial
                ? {
                    backgroundColor: c.cardBg,
                    borderColor: c.rule2,
                    color: c.ink,
                    fontFamily: theme.fonts.sans,
                  }
                : { backgroundColor: "#F7F8FA", border: "1px solid #CFE0D8", color: HEADER }
            }
            aria-label="Team timezone"
          >
            {options.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        ) : (
          timezone
        )}
      </span>
      {/* No trailing action control — the select itself is the affordance. A
          spacer keeps the row's right edge aligned with the other rows. */}
      <span className="w-[64px] shrink-0" aria-hidden />
    </div>
  );
}

/** "Dependencies ✓ · Notes ✓ · …" — the at-a-glance feature summary. */
function FeaturesSummary({ items }: { items: { label: string; enabled: boolean }[] }) {
  // Keep the line short like the gallery — show the first few features so the
  // summary stays scannable; the "manage" dialog has the full list.
  // Timed task messages show when on, so a lead sees the Telegram messages are live.
  const shown = [
    ...items.slice(0, 3),
    ...items.slice(3).filter((it) => it.enabled && it.label === TIMED_NOTICES_LABEL),
  ];
  return (
    <span data-testid="features-summary">
      {shown.map((it, i) => (
        <span key={it.label}>
          {i > 0 ? " · " : ""}
          {it.label} {it.enabled ? "✓" : "✗"}
        </span>
      ))}
    </span>
  );
}
